import React from 'react';
import styled from 'styled-components';
import { Clock, Check, Lock, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RelatedOffersSection = styled.section`
  margin-top: 4rem;
  padding: 3rem 0;
  background: rgba(15, 23, 42, 0.5);
  border-radius: 1.25rem;
  border: 1px solid rgba(56, 189, 248, 0.2);

  h3 {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 2rem;
    text-align: center;
    color: white;
  }
`;

const OffersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  padding: 0 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 1rem;
  }
`;

const OfferCard = styled.div`
  background: rgba(15, 23, 42, 0.9);
  border-radius: 1rem;
  padding: 2rem;
  border: 1px solid rgba(56, 189, 248, 0.3);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(56, 189, 248, 0.6);
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.8);
  }

  &.disabled {
    opacity: 0.7;
    cursor: not-allowed;
    
    &:hover {
      transform: none;
      border-color: rgba(56, 189, 248, 0.3);
    }
  }

  .offer-header {
    margin-bottom: 1.5rem;
  }

  .offer-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: white;
  }

  .offer-description {
    color: #9ca3af;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  .offer-badge {
    display: inline-block;
    background: #ef4444;
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .offer-price {
    margin-bottom: 1.5rem;
  }

  .price-current {
    font-size: 1.75rem;
    font-weight: 700;
    color: #38bdf8;
    margin-bottom: 0.5rem;
  }

  .price-original {
    font-size: 1rem;
    color: #9ca3af;
    text-decoration: line-through;
  }

  .price-discount {
    color: #10b981;
    font-weight: 600;
    font-size: 0.9rem;
  }

  .offer-features {
    list-style: none;
    margin-bottom: 1.5rem;
  }

  .feature-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;

    .icon {
      color: #10b981;
      flex-shrink: 0;
    }
  }

  .offer-timer {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    padding: 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    margin-bottom: 1rem;
    text-align: center;
  }

  .offer-restriction {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    padding: 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .icon {
      flex-shrink: 0;
    }
  }

  .offer-success {
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    color: #10b981;
    padding: 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .icon {
      flex-shrink: 0;
    }
  }

  .offer-button {
    width: 100%;
    background: #007AFF;
    color: white;
    font-weight: 600;
    padding: 0.75rem 2rem;
    border-radius: 0.5rem;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: #0056CC;
      transform: scale(1.02);
    }

    &:disabled {
      background: #6b7280;
      cursor: not-allowed;
      transform: none;
      
      &:hover {
        background: #6b7280;
        transform: none;
      }
    }
  }

  .offer-button.disabled {
    background: #6b7280;
    cursor: not-allowed;
    opacity: 0.7;
    
    &:hover {
      background: #6b7280;
      transform: none;
    }
  }
`;

const RelatedFlashOffers = ({ currentProduct, flashOffers, offerRestrictions, isAuthenticated }) => {
  const navigate = useNavigate();

  // Filtrer les offres flash pour ce produit
  const relatedOffers = flashOffers.filter(offer => 
    offer.product_slug === currentProduct?.slug && 
    offer.is_active && 
    !offer.is_expired
  );

  if (relatedOffers.length === 0) {
    return null;
  }

  const formatTimeLeft = (timeLeft) => {
    if (!timeLeft) return 'Expirée';
    return `${timeLeft.hours}h ${timeLeft.minutes}m ${timeLeft.seconds}s`;
  };

  const handleOfferClick = (offer) => {
    navigate(`/product/${currentProduct.slug}?offer=${offer.id}`);
  };

  return (
    <RelatedOffersSection>
      <h3>🔥 Offres Flash disponibles</h3>
      <OffersGrid>
        {relatedOffers.map((offer) => {
          const restriction = offerRestrictions[offer.id] || { isAvailable: true };
          const isDisabled = !restriction.isAvailable;
          
          return (
            <OfferCard 
              key={offer.id}
              className={isDisabled ? 'disabled' : ''}
              onClick={() => !isDisabled && handleOfferClick(offer)}
            >
              <div className="offer-header">
                <h4 className="offer-title">{offer.title}</h4>
                <p className="offer-description">{offer.description}</p>
                <span className="offer-badge">FLASH DEAL</span>
              </div>

              <div className="offer-price">
                <div className="price-current">
                  {Number(offer.discounted_price).toLocaleString()} FCFA
                </div>
                {offer.original_price && (
                  <div className="price-original">
                    {Number(offer.original_price).toLocaleString()} FCFA
                  </div>
                )}
                <div className="price-discount">
                  -{offer.discount_percentage}%
                </div>
              </div>

              <ul className="offer-features">
                {offer.includes && offer.includes.slice(0, 3).map((item, i) => (
                  <li key={i} className="feature-item">
                    <Check className="icon" size={16} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              {/* Messages de restriction */}
              {isDisabled && restriction.reason && (
                <div className="offer-restriction">
                  <Lock className="icon" size={16} />
                  <span>{restriction.reason}</span>
                </div>
              )}

              {!isDisabled && isAuthenticated && (
                <div className="offer-success">
                  <CheckCircle className="icon" size={16} />
                  <span>Disponible pour vous</span>
                </div>
              )}

              {/* Timer */}
              {offer.timeLeft && (
                <div className="offer-timer">
                  ⏰ Temps restant: {formatTimeLeft(offer.timeLeft)}
                </div>
              )}

              <button 
                className={`offer-button ${isDisabled ? 'disabled' : ''}`}
                disabled={isDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDisabled) {
                    handleOfferClick(offer);
                  }
                }}
              >
                {isDisabled ? (
                  restriction.alreadyUsed ? 'DÉJÀ UTILISÉE' : 'INDISPONIBLE'
                ) : (
                  'VOIR L\'OFFRE'
                )}
              </button>
            </OfferCard>
          );
        })}
      </OffersGrid>
    </RelatedOffersSection>
  );
};

export default RelatedFlashOffers;
